import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { LoaderIcon, PencilIcon, TrashIcon, PlusIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { sandboxConfigApi } from '@/api/sandboxConfig.api';
import { Button } from '@/components/ui/button';
import { UserName } from '@/components/UserName';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { formatTimestamp } from '@/utils/utils';
import { MountPointType } from '@/proto/agentplatform/v1/sandbox_pb';
import { useSecrets } from '@/pages/credentials/hooks/useSecrets';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-accent rounded transition-colors"
      title="复制"
    >
      {copied ? (
        <svg
          className="w-3.5 h-3.5 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg
          className="w-3.5 h-3.5 text-muted-foreground"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
}

function InfoItem({
  label,
  children,
  copyable,
}: {
  label: string;
  children: React.ReactNode;
  copyable?: string;
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <div className="flex-1">{children}</div>
        {copyable && <CopyButton text={copyable} />}
      </div>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

const ServerIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
    />
  </svg>
);

const CpuIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
  </svg>
);

const MemoryIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 19v-3m4 3v-5m4 5v-7m4 7v-9" />
    <rect x="2" y="19" width="20" height="2" rx="1" />
  </svg>
);

const SettingsIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
    />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const VariableIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 21s-4-3-4-9 4-9 4-9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 3s4 3 4 9-4 9-4 9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18" />
  </svg>
);

const DatabaseIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 5v14c0 1.657 4.03 3 9 3s9-1.343 9-3V5"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12c0 1.657 4.03 3 9 3s9-1.343 9-3" />
  </svg>
);

type TabId = 'overview' | 'instances';

function OverviewTab({ classData }: { classData: any }) {
  const { template } = classData;
  const specId = template?.sandboxSpecId;
  const envId = template?.sandboxEnvironmentId;

  const { data: secretsRes } = useSecrets(1, 100);
  const availableSecretIds = new Set((secretsRes?.data ?? []).map((s) => s.id));
  const secretsLoaded = secretsRes !== undefined;

  const { data: specData } = useQuery({
    queryKey: ['sandbox-spec', specId],
    queryFn: () => sandboxConfigApi.getSandboxSpec(specId),
    enabled: !!specId,
  });

  const { data: envData } = useQuery({
    queryKey: ['sandbox-environment', envId],
    queryFn: () => sandboxConfigApi.getSandboxEnvironment(envId),
    enabled: !!envId,
  });

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            基本信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="类名称">
              <span className="font-medium">{classData.name}</span>
            </InfoItem>
            <InfoItem label="类型">
              <Badge variant="secondary">{template?.type || '-'}</Badge>
            </InfoItem>
          </div>
          <InfoItem label="描述">
            <span className="text-sm">{classData.description || '暂无描述'}</span>
          </InfoItem>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="沙箱模版 ID" copyable={classData.id}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{classData.id}</code>
            </InfoItem>
            <InfoItem label="租户 ID" copyable={classData.tenantId}>
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                {classData.tenantId}
              </code>
            </InfoItem>
            <InfoItem label="创建用户">
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono break-all">
                <UserName userId={classData.userId} />
              </code>
            </InfoItem>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CpuIcon className="w-4 h-4" />
            资源规格
          </CardTitle>
        </CardHeader>
        <CardContent>
          {specData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InfoItem label="名称">
                  <span className="font-medium">{specData.name}</span>
                </InfoItem>
                <InfoItem label="标签">
                  {specData.tags && specData.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {specData.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-none bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground ring-1 ring-inset ring-border/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </InfoItem>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InfoItem label="CPU">
                  <span className="text-sm">
                    {specData.specConfig?.cpuMilliCores
                      ? `${(specData.specConfig.cpuMilliCores / 1000).toFixed(1)} 核`
                      : '-'}
                  </span>
                </InfoItem>
                <InfoItem label="内存">
                  <span className="text-sm">
                    {specData.specConfig?.memoryMb
                      ? specData.specConfig.memoryMb >= 1000
                        ? `${(specData.specConfig.memoryMb / 1000).toFixed(1)} GB`
                        : `${specData.specConfig.memoryMb} MB`
                      : '-'}
                  </span>
                </InfoItem>
              </div>
            </div>
          ) : specId ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <LoaderIcon className="h-4 w-4 animate-spin" />
              加载中...
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">未配置资源规格</span>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MemoryIcon className="w-4 h-4" />
            环境配置
          </CardTitle>
        </CardHeader>
        <CardContent>
          {envData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoItem label="名称">
                <span className="font-medium">{envData.name}</span>
              </InfoItem>
              <InfoItem label="标签">
                {envData.tags && envData.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {envData.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="inline-flex items-center rounded-none bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground ring-1 ring-inset ring-border/40"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">-</span>
                )}
              </InfoItem>
            </div>
          ) : envId ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <LoaderIcon className="h-4 w-4 animate-spin" />
              加载中...
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">未配置环境</span>
          )}
        </CardContent>
      </Card>

      {classData.params && classData.params.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" />
              参数配置
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">名称</th>
                    <th className="px-4 py-2.5 text-left font-medium">类型</th>
                    <th className="px-4 py-2.5 text-left font-medium">目标</th>
                    <th className="px-4 py-2.5 text-left font-medium">值</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {classData.params.map((param: any, idx: number) => (
                    <tr key={idx} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs">{param.name}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-xs">
                          {param.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{param.target}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {param.paramValue?.value || JSON.stringify(param.paramValue?.case) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={VariableIcon}
              title="暂无参数配置"
              description="该沙箱模版尚未配置任何参数"
            />
          </CardContent>
        </Card>
      )}

      {(() => {
        const mounts = (classData.template?.mountPoints ?? []).filter(
          (m: { type: number }) => m.type === MountPointType.OBJECT_STORAGE
        );
        if (mounts.length === 0) return null;
        return (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <DatabaseIcon className="w-4 h-4" />
                存储挂载
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium">Bucket</th>
                      <th className="px-4 py-2.5 text-left font-medium">子目录</th>
                      <th className="px-4 py-2.5 text-left font-medium">本地目录</th>
                      <th className="px-4 py-2.5 text-left font-medium">权限</th>
                      <th className="px-4 py-2.5 text-left font-medium">凭证 ID</th>
                      <th className="px-4 py-2.5 text-left font-medium">备注</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {mounts.map(
                      (
                        mp: {
                          source?: string;
                          subDir?: string;
                          mountPath?: string;
                          readOnly?: boolean;
                          secretId?: string;
                          note?: string;
                        },
                        idx: number
                      ) => {
                        const secretUnavailable =
                          secretsLoaded && !!mp.secretId && !availableSecretIds.has(mp.secretId);
                        return (
                          <tr
                            key={idx}
                            className={`hover:bg-muted/30 transition-colors ${secretUnavailable ? 'bg-destructive/5' : ''}`}
                          >
                            <td className="px-4 py-2.5 font-mono text-xs">{mp.source || '-'}</td>
                            <td className="px-4 py-2.5 font-mono text-xs">{mp.subDir || '/'}</td>
                            <td className="px-4 py-2.5 font-mono text-xs">{mp.mountPath || '-'}</td>
                            <td className="px-4 py-2.5">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${mp.readOnly ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}
                              >
                                {mp.readOnly ? '只读' : '读写'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground max-w-[160px]">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate">{mp.secretId || '-'}</span>
                                {secretUnavailable && (
                                  <span
                                    title="该凭证已被删除或不可用"
                                    className="shrink-0 inline-flex items-center gap-1 text-destructive"
                                  >
                                    <svg
                                      className="w-3.5 h-3.5"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v3m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                      />
                                    </svg>
                                    <span className="text-xs font-medium">凭证不可用</span>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground">
                              {mp.note || '-'}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}

function InstancesTab({
  instancesData,
  instancesLoading,
  instancesPage,
  setInstancesPage,
  instancesPageSize,
  setInstancesPageSize,
  onDelete,
  classUid,
}: {
  instancesData: any;
  instancesLoading: boolean;
  instancesPage: number;
  setInstancesPage: (p: number) => void;
  instancesPageSize: number;
  setInstancesPageSize: (s: number) => void;
  onDelete: (id: string, name: string) => void;
  classUid: string;
}) {
  const navigate = useNavigate();

  if (instancesLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const instances = instancesData?.data || [];

  if (instances.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <EmptyState icon={ServerIcon} title="暂无实例" description="该沙箱模版尚未创建任何实例" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <ServerIcon className="w-4 h-4" />
          实例列表
        </CardTitle>
        <span className="text-xs text-muted-foreground">
          共 {instancesData?.total || instances.length} 个实例
        </span>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">名称</th>
                <th className="px-4 py-2.5 text-left font-medium">类型</th>
                <th className="px-4 py-2.5 text-left font-medium">创建时间</th>
                <th className="px-4 py-2.5 text-left font-medium">更新时间</th>
                <th className="px-4 py-2.5 text-right font-medium w-24">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {instances.map((instance: any) => (
                <tr
                  key={instance.id}
                  className="hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/plugins/sandboxes/instances/${instance.id}`)}
                >
                  <td className="px-4 py-2.5 font-medium">
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="text-primary hover:text-primary/80 font-medium text-left truncate block"
                        onClick={() => navigate(`/plugins/sandboxes/instances/${instance.id}`)}
                      >
                        {instance.name}
                      </button>
                      <span className="text-xs text-muted-foreground/60">ID: {instance.id}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className="text-xs">
                      {instance.type === 'vm' ? '虚拟机' : '容器'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {formatTimestamp(Number(instance.createdAt))}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {formatTimestamp(Number(instance.updatedAt))}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/plugins/sandboxes/create-instance-from-class/${classUid}?instanceId=${instance.id}`
                          );
                        }}
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(instance.id, instance.name);
                        }}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/10 gap-4">
          <p className="text-sm text-muted-foreground">
            共{' '}
            <span className="font-medium text-foreground">
              {instancesData?.total || instances.length}
            </span>{' '}
            条
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">每页</span>
              <select
                value={instancesPageSize}
                onChange={(e) => {
                  setInstancesPageSize(Number(e.target.value));
                  setInstancesPage(1);
                }}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs text-muted-foreground">条</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={instancesPage <= 1}
                onClick={() => setInstancesPage(Math.max(1, instancesPage - 1))}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </Button>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-xs">
                <span className="font-medium">{instancesPage}</span>
                <span className="text-muted-foreground">/</span>
                <span className="text-muted-foreground">
                  {Math.max(1, Math.ceil((instancesData?.total || 0) / instancesPageSize))}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={
                  instancesPage >=
                  Math.max(1, Math.ceil((instancesData?.total || 0) / instancesPageSize))
                }
                onClick={() => setInstancesPage(instancesPage + 1)}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SandboxClassDetailPage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState<{ uid: string; name: string } | null>(
    null
  );
  const [instancesPage, setInstancesPage] = useState(1);
  const [instancesPageSize, setInstancesPageSize] = useState(10);
  const queryClient = useQueryClient();

  const { data: classData, isLoading } = useQuery({
    queryKey: ['sandbox-class', uid],
    queryFn: () => sandboxClassApi.getSandboxClass(uid!),
    enabled: !!uid,
  });

  const { data: instancesData, isLoading: instancesLoading } = useQuery({
    queryKey: ['sandbox-instances-by-class', uid, instancesPage, instancesPageSize],
    queryFn: () =>
      sandboxApi.listSandboxes(
        create(ListRequestSchema, {
          page: instancesPage,
          pageSize: instancesPageSize,
          filter: { class_id: uid || '' },
        })
      ),
    enabled: !!uid,
  });

  const deleteMutation = useMutation({
    mutationFn: () => sandboxClassApi.deleteSandboxClass(uid!),
    onSuccess: () => navigate('/plugins/sandboxes?tab=class'),
    onError: (error) => toast.error(`删除失败：${fullErrorText(error)}`),
  });

  const deleteInstanceMutation = useMutation({
    mutationFn: (instanceUid: string) => sandboxApi.deleteSandbox(instanceUid),
    onSuccess: () => {
      setInstanceToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['sandbox-instances-by-class', uid] });
    },
    onError: (error) => toast.error(`删除失败：${fullErrorText(error)}`),
  });

  const tabs = [
    { id: 'overview' as TabId, label: '概览' },
    { id: 'instances' as TabId, label: '实例' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-24" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-destructive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">沙箱模版不存在</h3>
              <p className="text-muted-foreground mb-4">该沙箱模版可能已被删除或不存在</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/plugins/sandboxes')}
            className="h-8 w-8 p-0"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{classData.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {classData.description || '暂无描述'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate(`/plugins/sandboxes/create-instance-from-class/${uid}`)}
          >
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            创建实例
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/plugins/sandboxes/classes/${uid}/edit`)}
          >
            <PencilIcon className="mr-1.5 h-3.5 w-3.5" />
            编辑
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            className="text-destructive hover:text-destructive"
          >
            <TrashIcon className="mr-1.5 h-3.5 w-3.5" />
            删除
          </Button>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={(id) => setActiveTab(id as TabId)} />

      <div className="animate-in fade-in duration-300">
        {activeTab === 'overview' && <OverviewTab classData={classData} />}
        {activeTab === 'instances' && (
          <InstancesTab
            instancesData={instancesData}
            instancesLoading={instancesLoading}
            instancesPage={instancesPage}
            setInstancesPage={setInstancesPage}
            instancesPageSize={instancesPageSize}
            setInstancesPageSize={setInstancesPageSize}
            onDelete={(id, name) => setInstanceToDelete({ uid: id, name })}
            classUid={uid!}
          />
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除沙箱模版「{classData.name}」吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!instanceToDelete}
        onOpenChange={(open) => !open && setInstanceToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除实例</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除实例「{instanceToDelete?.name}」吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInstanceToDelete(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                instanceToDelete && deleteInstanceMutation.mutate(instanceToDelete.uid)
              }
              disabled={deleteInstanceMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteInstanceMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
